import React, { useState } from 'react';
import { useCurrentUser } from '@/lib/api/hooks/auth';
import { useUpdateAdminUser, useChangePassword } from '@/lib/api/hooks/admin-user';
import { Button } from '@/components/ui';
import { Loader } from '@/components/ui/Loader';
import { showToast } from '@/lib/toast';
import EditProfileModal from '@/pages/my-profile/modals/EditProfileModal';
import ChangePasswordModal from '@/pages/my-profile/modals/ChangePasswordModal';

const MyProfile: React.FC = () => {
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [isChangePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
    const { data: currentUser, isLoading, error } = useCurrentUser();
    const updateAdminUserMutation = useUpdateAdminUser();
    const changePasswordMutation = useChangePassword();

    const handleUpdateProfile = async (data: { firstName: string; lastName: string }) => {
        try {
            await updateAdminUserMutation.mutateAsync(data);
            // No need to call refetch() - the mutation hook handles cache updates
            setEditModalOpen(false);
        } catch (error) {
            console.error('Error updating profile:', error);
            showToast.error(error instanceof Error ? error.message : 'Failed to update profile');
        }
    };

    const handleChangePassword = async (data: { oldPassword: string; newPassword: string }) => {
        try {
            await changePasswordMutation.mutateAsync(data);
            setChangePasswordModalOpen(false);
        } catch (error) {
            console.error('Error changing password:', error);
            showToast.error(error instanceof Error ? error.message : 'Failed to change password');
        }
    };

    if (isLoading) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
                <Loader size="md" variant="wine" message="Loading profile..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
                <div className="text-center">
                    <div className="inline-block bg-red-100 p-4 rounded-full mb-4">
                        <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Profile</h3>
                    <p className="text-gray-600">{error.message || 'Something went wrong'}</p>
                </div>
            </div>
        );
    }

    if (!currentUser) {
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
                <div className="text-center">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">No Profile Data</h3>
                    <p className="text-gray-600">Unable to load your profile information.</p>
                </div>
            </div>
        );
    }

    // Get user profile data
    const firstName = currentUser.userProfile?.firstName  || '';
    const lastName = currentUser.userProfile?.lastName || '';
    const email = currentUser.userProfile?.emailAddress ||  '';
    const role = currentUser.userRole || 'User';

    // Generate avatar initials
    const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();

    // Format role display
    const formatRole = (role: string) => {
        switch (role) {
            case 'SUPER_ADMIN':
                return 'Super Administrator';
            case 'ADMIN_STAFF':
                return 'Administrator';
            case 'EDITOR':
                return 'Editor';
            case 'VIEWER':
                return 'Viewer';
            default:
                return role;
        }
    };

    return (
        <>
            <div className="flex flex-col gap-8 lg:gap-12 p-4 md:p-8 lg:p-12 w-full mx-auto">
                {/* Page Header */}
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
                    <p className="text-sm text-gray-600 mt-1">Manage your personal information</p>
                </div>

                {/* Profile Card */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {/* Header Section */}
                    <div className="bg-gradient-to-r from-wine-100 to-wine-200 p-8">
                        <div className="flex items-center gap-6">
                            <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center shadow-lg">
                                <span className="text-3xl font-bold text-wine-100">{initials || '??'}</span>
                            </div>
                            <div className="flex-1">
                                <h2 className="text-2xl font-bold text-white">{firstName} {lastName}</h2>
                                <p className="text-white/90 mt-1">{email}</p>
                                <div className="mt-3">
                                    <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-sm font-medium rounded-md">
                                        {formatRole(role)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Profile Information */}
                    <div className="p-8">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-semibold text-gray-900">Profile Information</h3>
                            <div className="flex items-center gap-3">
                                <Button
                                    variant="secondary"
                                    onClick={() => setChangePasswordModalOpen(true)}
                                >
                                    Change Password
                                </Button>
                                <Button 
                                    variant="primary" 
                                    onClick={() => setEditModalOpen(true)}
                                >
                                    Edit Profile
                                </Button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">First Name</label>
                                <p className="text-base font-semibold text-gray-900">{firstName}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Last Name</label>
                                <p className="text-base font-semibold text-gray-900">{lastName}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Email Address</label>
                                <p className="text-base font-semibold text-gray-900">{email}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-500 mb-1">Role</label>
                                <p className="text-base font-semibold text-gray-900">{formatRole(role)}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <EditProfileModal
                isOpen={isEditModalOpen}
                firstName={firstName}
                lastName={lastName}
                email={email}
                onClose={() => setEditModalOpen(false)}
                onUpdate={handleUpdateProfile}
                isUpdating={updateAdminUserMutation.isPending}
            />

            <ChangePasswordModal
                isOpen={isChangePasswordModalOpen}
                onClose={() => setChangePasswordModalOpen(false)}
                onUpdate={handleChangePassword}
                isUpdating={changePasswordMutation.isPending}
            />
        </>
    );
};

export default MyProfile;

